import {
  AlertCircle,
  Archive,
  ArchiveRestore,
  ArrowDown,
  ArrowRight,
  ArrowUp,
  Ban,
  Blocks,
  CalendarClock,
  CalendarCheck2,
  Check,
  CheckCircle2,
  ChevronDown,
  CircleDot,
  CircleHelp,
  ClipboardCheck,
  Clock3,
  Download,
  Edit3,
  FileCheck2,
  Filter,
  History,
  LayoutDashboard,
  ListChecks,
  LogIn,
  LogOut,
  Menu,
  MoreHorizontal,
  Plus,
  RefreshCw,
  RotateCcw,
  Save,
  Search,
  ShieldCheck,
  ShieldAlert,
  Sparkles,
  Target,
  Trash2,
  UserRound,
  Users,
  X,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { api, formatDate, initials } from "./api";
import {
  currentReturnTo,
  navigationUrl,
  readNavigationState,
  removeSearchParameter,
} from "./navigation-state";
import {
  buildWorkBlockExecution,
  getPriorityWorkBlocks,
} from "./work-block-execution";

const navItems = [
  { id: "overview", label: "总览", icon: LayoutDashboard },
  { id: "today", label: "今日待办", icon: CalendarCheck2 },
  { id: "work-blocks", label: "工作块", icon: Blocks },
  { id: "people", label: "人员", icon: Users },
  { id: "tasks", label: "任务", icon: ListChecks },
  { id: "events", label: "更新记录", icon: History },
  { id: "admin", label: "权限管理", icon: ShieldCheck, adminOnly: true },
];

function can(auth, page, action = "view") {
  if (page === "admin") return Boolean(auth?.user?.is_admin);
  return Boolean(auth?.permissions?.[page]?.[action]);
}

const statusClass = {
  未开始: "neutral",
  进行中: "blue",
  阻塞: "red",
  等待: "amber",
  已完成: "green",
  已取消: "muted",
  正常: "green",
  推进中: "blue",
  风险: "amber",
  卡住: "red",
  完成: "green",
  初始化中: "neutral",
  待确认: "muted",
};

const priorityClass = {
  P0: "priority-p0",
  P1: "priority-p1",
  P2: "priority-p2",
  P3: "priority-p3",
  待确认: "priority-pending",
};

function monthGoalLabel(period, withMethod = true) {
  const match = String(period || "").match(/^\d{4}-(\d{2})$/);
  const label = match ? `${Number(match[1])}月末目标` : "月末目标";
  return withMethod ? `${label} · 以终为始` : label;
}

function Badge({ children, tone, className = "" }) {
  return (
    <span className={`badge ${tone || statusClass[children] || "neutral"} ${className}`}>
      {children}
    </span>
  );
}

function Avatar({ name, size = "md" }) {
  return (
    <span className={`avatar avatar-${size}`} aria-label={name || "负责人待确认"}>
      {initials(name)}
    </span>
  );
}

function WorkBlockOwners({ block }) {
  const primaryName = block.owner_name || null;
  const assistantNames = [
    ...new Set(
      (block.people || [])
        .filter((person) => person.name && person.id !== block.owner_id)
        .map((person) => person.name),
    ),
  ];

  if (!assistantNames.length) {
    return <span className="owner-name-only">{primaryName || "待确认"}</span>;
  }

  return (
    <span className="owner-role-list">
      <span><small>主要：</small>{primaryName || "待确认"}</span>
      <span><small>辅助：</small>{assistantNames.join("、")}</span>
    </span>
  );
}

function EmptyState({ icon: Icon = CircleHelp, title, body, action }) {
  return (
    <div className="empty-state">
      <Icon size={22} />
      <strong>{title}</strong>
      <p>{body}</p>
      {action}
    </div>
  );
}

function ProgressBar({ value = 0, manual = false, compact = false }) {
  const safe = Math.max(0, Math.min(100, Number(value) || 0));
  return (
    <div className={`progress-wrap ${compact ? "compact" : ""}`}>
      <div className="progress-label">
        <span>{safe}%</span>
        {manual && <span className="manual-flag">手动</span>}
      </div>
      <div className="progress-track" aria-label={`进度 ${safe}%`}>
        <span style={{ width: `${safe}%` }} />
      </div>
    </div>
  );
}

function Metric({ icon: Icon, label, value, tone = "" }) {
  return (
    <div className={`metric ${tone}`}>
      <div className="metric-icon"><Icon size={17} /></div>
      <div>
        <span>{label}</span>
        <strong>{value}</strong>
      </div>
    </div>
  );
}

function SectionHeader({ title, description, action }) {
  return (
    <div className="section-header">
      <div>
        <h2>{title}</h2>
        {description && <p>{description}</p>}
      </div>
      {action}
    </div>
  );
}

function TaskTitle({ task, onOpen }) {
  return (
    <button className="task-title" onClick={() => onOpen("task", task.id)}>
      <span>{task.title}</span>
      {task.confirmation_status === "待确认" && (
        <span className="confirm-dot" title="字段或状态待确认" />
      )}
    </button>
  );
}

function TaskActions({ task, onEdit, onAction, permissions }) {
  const terminal = ["已完成", "已取消"].includes(task.status);
  const allowEdit = Boolean(permissions?.edit);
  const allowOperate = Boolean(permissions?.operate);
  if (!allowEdit && !allowOperate) return null;
  return (
    <div className="row-actions">
      {allowOperate && (
        <button
          className="icon-button"
          title={task.status === "已完成" ? "重新打开" : "完成"}
          onClick={() => onAction(task, task.status === "已完成" ? "reopen" : "complete")}
        >
          {task.status === "已完成" ? <RotateCcw size={16} /> : <Check size={16} />}
        </button>
      )}
      {allowOperate && !terminal && (
        <button className="icon-button" title="设为阻塞" onClick={() => onAction(task, "block")}>
          <ShieldAlert size={16} />
        </button>
      )}
      {allowEdit && (
        <button className="icon-button" title="编辑" onClick={() => onEdit(task)}>
          <Edit3 size={16} />
        </button>
      )}
    </div>
  );
}

function TaskTable({
  tasks,
  onOpen,
  onEdit,
  onAction,
  permissions,
  compact = false,
  empty = "没有符合条件的任务",
}) {
  if (!tasks.length) {
    return (
      <EmptyState
        icon={ListChecks}
        title={empty}
        body="调整筛选条件，或新增一条真实任务。"
      />
    );
  }
  return (
    <div className="table-scroll">
      <table className={`data-table task-table ${compact ? "compact-table" : ""}`}>
        <thead>
          <tr>
            <th>任务</th>
            <th>工作块</th>
            <th>负责人</th>
            <th>优先级</th>
            <th>状态</th>
            <th>截止</th>
            <th className="action-column">操作</th>
          </tr>
        </thead>
        <tbody>
          {tasks.map((task) => (
            <tr key={task.id} className={task.overdue ? "overdue-row" : ""}>
              <td>
                <TaskTitle task={task} onOpen={onOpen} />
                {(task.blocker || task.next_action) && (
                  <div className="row-subtext">
                    {task.blocker ? `卡点：${task.blocker}` : `下一步：${task.next_action}`}
                  </div>
                )}
              </td>
              <td>
                <span className="muted-text">{task.work_block_name || "待归类"}</span>
              </td>
              <td>
                <span className="person-cell">
                  <Avatar name={task.assignee_name} size="sm" />
                  {task.assignee_name || "待确认"}
                </span>
              </td>
              <td><span className={`priority ${priorityClass[task.priority]}`}>{task.priority}</span></td>
              <td><Badge>{task.status}</Badge></td>
              <td>
                <span className={task.overdue ? "danger-text" : "muted-text"}>
                  {task.overdue && "逾期 · "}
                  {formatDate(task.due_date)}
                </span>
              </td>
              <td>
                <TaskActions task={task} onEdit={onEdit} onAction={onAction} permissions={permissions} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Overview({ data, onOpen, onEditTask, onAction, onNewTask, auth }) {
  const { dashboard } = data;
  return (
    <>
      <SectionHeader
        title="今天先看这些"
        description={`数据截至 ${formatDate(data.generated_at)}；未确认字段不会被系统自动猜测。`}
        action={can(auth, "tasks", "create") && (
          <button className="primary-button" onClick={onNewTask}>
            <Plus size={16} /> 新增任务
          </button>
        )}
      />
      <div className="metrics-grid">
        <Metric icon={AlertCircle} label="逾期" value={dashboard.summary.overdue} tone="danger" />
        <Metric icon={ShieldAlert} label="阻塞" value={dashboard.summary.blocked} tone="danger-soft" />
        <Metric icon={ClipboardCheck} label="待拍板" value={dashboard.summary.decisions} tone="amber" />
        <Metric icon={CalendarClock} label="7天内到期" value={dashboard.summary.due_soon} />
        <Metric icon={CheckCircle2} label="近期完成" value={dashboard.recent_completed.length} tone="green" />
      </div>

      <div className="overview-grid">
        <section className="panel focus-panel">
          <div className="panel-heading">
            <div>
              <span className="eyebrow">今日 / 本周</span>
              <h3>优先推进</h3>
            </div>
            <Target size={18} />
          </div>
          <div className="focus-list">
            {dashboard.focus.slice(0, 8).map((task) => (
              <button key={task.id} className="focus-item" onClick={() => onOpen("task", task.id)}>
                <span className={`priority ${priorityClass[task.priority]}`}>{task.priority}</span>
                <span className="focus-copy">
                  <strong>{task.title}</strong>
                  <small>{task.work_block_name || "待归类"} · {task.assignee_name || "负责人待确认"}</small>
                </span>
                <ArrowRight size={15} />
              </button>
            ))}
          </div>
        </section>

        <section className="panel decision-panel">
          <div className="panel-heading">
            <div>
              <span className="eyebrow">只等李超</span>
              <h3>待拍板事项</h3>
            </div>
            <ClipboardCheck size={18} />
          </div>
          <div className="decision-list">
            {data.decisions.slice(0, 8).map((item) => (
              <button key={item.id} onClick={() => onOpen("work-block", item.work_block_id)}>
                <span>{item.work_block_name}</span>
                <strong>{item.text}</strong>
              </button>
            ))}
          </div>
        </section>
      </div>

      <section className="panel section-panel">
        <div className="panel-heading">
          <div>
            <span className="eyebrow">风险队列</span>
            <h3>逾期与阻塞</h3>
          </div>
          <span className="subtle-count">{dashboard.overdue.length + dashboard.blocked.length} 项</span>
        </div>
        <TaskTable
          compact
          tasks={[
            ...dashboard.overdue,
            ...dashboard.blocked.filter((task) => !dashboard.overdue.some((item) => item.id === task.id)),
          ].slice(0, 10)}
          onOpen={onOpen}
          onEdit={onEditTask}
          onAction={onAction}
          permissions={auth.permissions.tasks}
          empty="当前没有逾期或阻塞"
        />
      </section>

      <section className="panel recent-strip">
        <div className="panel-heading">
          <div>
            <span className="eyebrow">形成闭环</span>
            <h3>最近完成</h3>
          </div>
        </div>
        <div className="completed-list">
          {dashboard.recent_completed.slice(0, 6).map((task) => (
            <button key={task.id} onClick={() => onOpen("task", task.id)}>
              <CheckCircle2 size={16} />
              <span>
                <strong>{task.title}</strong>
                <small>{task.evidence || "验收证据待补充"}</small>
              </span>
            </button>
          ))}
        </div>
      </section>
    </>
  );
}

function TodayView({ data, onOpen, onAction, onSaved, onToast, auth }) {
  const plan = data.today_plan;
  const [goal, setGoal] = useState(plan.goal || "");
  const [selectedTask, setSelectedTask] = useState("");
  const [saving, setSaving] = useState("");

  useEffect(() => setGoal(plan.goal || ""), [plan.goal, plan.updated_at]);

  const availableTasks = data.tasks.filter(
    (task) =>
      !["已完成", "已取消"].includes(task.status) &&
      !plan.items.some((item) => item.task_id === task.id),
  );

  async function request(path, options, message, key) {
    setSaving(key);
    try {
      await api(path, options);
      await onSaved(message);
      return true;
    } catch (requestError) {
      onToast(requestError.message);
      return false;
    } finally {
      setSaving("");
    }
  }

  async function recommend() {
    if (
      plan.items.length &&
      !window.confirm("重新生成会替换当前待确认清单，是否继续？")
    ) {
      return;
    }
    await request(
      "/api/today-plan/recommend",
      {
        method: "POST",
        body: JSON.stringify({ plan_date: plan.plan_date, limit: 5, goal }),
      },
      "已生成今日建议，等待你确认",
      "recommend",
    );
  }

  async function saveItems(items, message, nextGoal = goal) {
    return request(
      "/api/today-plan",
      {
        method: "PATCH",
        body: JSON.stringify({
          plan_date: plan.plan_date,
          goal: nextGoal,
          items: items.map((item) => ({
            task_id: item.task_id,
            reason: item.reason,
            source: item.source,
          })),
        }),
      },
      message,
      "edit",
    );
  }

  async function addTask() {
    const task = data.tasks.find((item) => item.id === selectedTask);
    if (!task) return;
    const added = await saveItems(
      [
        ...plan.items,
        {
          task_id: task.id,
          task,
          reason: "李超手工加入",
          source: "手工选择",
        },
      ],
      "已加入今日待办，等待重新确认",
    );
    if (added !== false) setSelectedTask("");
  }

  async function moveItem(index, direction) {
    const target = index + direction;
    if (target < 0 || target >= plan.items.length) return;
    const next = [...plan.items];
    [next[index], next[target]] = [next[target], next[index]];
    await saveItems(next, "今日待办顺序已调整");
  }

  async function removeItem(index) {
    const item = plan.items[index];
    if (!window.confirm(`确认从今日待办移除“${item.task.title}”？任务本身不会删除。`)) {
      return;
    }
    await saveItems(
      plan.items.filter((_, itemIndex) => itemIndex !== index),
      "已从今日待办移除",
    );
  }

  async function saveGoal() {
    await saveItems(plan.items, "今日目标已保存", goal);
  }

  async function confirmPlan() {
    await request(
      "/api/today-plan/confirm",
      {
        method: "POST",
        body: JSON.stringify({ plan_date: plan.plan_date }),
      },
      "今日安排已确认",
      "confirm",
    );
  }

  const statusLabel =
    plan.status === "confirmed"
      ? "已确认"
      : plan.status === "draft"
        ? "待你确认"
        : "尚未制定";

  return (
    <>
      <SectionHeader
        title="今日待办"
        description="从全部任务中收敛今天真正要推进的事项。推荐只是草案，以你的最终确认为准。"
        action={
          <div className="header-actions">
            <Badge tone={plan.status === "confirmed" ? "green" : "amber"}>
              {statusLabel}
            </Badge>
            {can(auth, "today", "create") && (
              <button
                className="secondary-button"
                disabled={Boolean(saving)}
                onClick={recommend}
              >
                <Sparkles size={16} />
                {plan.status === "not_started" ? "生成今日建议" : "重新生成建议"}
              </button>
            )}
            {can(auth, "today", "operate") && plan.items.length > 0 && (
              <button
                className="primary-button"
                disabled={Boolean(saving) || plan.status === "confirmed"}
                onClick={confirmPlan}
              >
                <Check size={16} />
                {plan.status === "confirmed" ? "今日安排已确认" : "确认今日安排"}
              </button>
            )}
          </div>
        }
      />

      <section className="panel today-goal-panel">
        <div>
          <span className="eyebrow">最重要的结果</span>
          <h3>今天结束时，什么结果最值得交付？</h3>
        </div>
        <div className="today-goal-editor">
          <input
            value={goal}
            disabled={!can(auth, "today", "edit")}
            onChange={(event) => setGoal(event.target.value)}
            placeholder="例如：完成税务口径确认，并明确下一步责任人"
          />
          {can(auth, "today", "edit") && (
            <button
              className="secondary-button"
              disabled={Boolean(saving) || goal === (plan.goal || "")}
              onClick={saveGoal}
            >
              <Save size={16} /> 保存目标
            </button>
          )}
        </div>
        <small>
          {plan.status === "confirmed"
            ? `${plan.confirmed_by || "李超"}于 ${formatDate(plan.confirmed_at)} 确认`
            : plan.recommended_by
              ? `当前建议来源：${plan.recommended_by}`
              : "尚未生成或确认今日安排"}
        </small>
      </section>

      <div className="today-layout">
        <section className="panel today-plan-panel">
          <div className="panel-heading">
            <div>
              <span className="eyebrow">{plan.plan_date}</span>
              <h3>今天主要做这 {plan.items.length} 项</h3>
            </div>
            <span className="subtle-count">建议控制在 3–5 项</span>
          </div>
          {plan.items.length ? (
            <div className="today-plan-list">
              {plan.items.map((item, index) => (
                <article
                  className={`today-plan-item ${item.task.status === "已完成" ? "completed" : ""}`}
                  key={item.task_id}
                >
                  <span className="today-rank">{index + 1}</span>
                  <div className="today-task-copy">
                    <button onClick={() => onOpen("task", item.task_id)}>
                      {item.task.title}
                    </button>
                    <div className="today-task-meta">
                      <span className={`priority ${priorityClass[item.task.priority]}`}>
                        {item.task.priority}
                      </span>
                      <span>{item.task.work_block_name || "待归类"}</span>
                      <span>{item.task.assignee_name || "负责人待确认"}</span>
                      <Badge>{item.task.status}</Badge>
                      <span className={item.task.overdue ? "danger-text" : ""}>
                        {item.task.overdue ? "逾期 · " : "截止 · "}
                        {formatDate(item.task.due_date)}
                      </span>
                    </div>
                    <p>
                      <Sparkles size={14} />
                      {item.reason || "由李超手工加入今日待办"}
                    </p>
                  </div>
                  <div className="today-item-actions">
                    {can(auth, "tasks", "operate") &&
                      item.task.status !== "已完成" && (
                        <button
                          className="icon-button"
                          title="完成任务"
                          onClick={() => onAction(item.task, "complete")}
                        >
                          <Check size={16} />
                        </button>
                      )}
                    {can(auth, "today", "edit") && (
                      <>
                        <button
                          className="icon-button"
                          title="上移"
                          disabled={index === 0 || Boolean(saving)}
                          onClick={() => moveItem(index, -1)}
                        >
                          <ArrowUp size={16} />
                        </button>
                        <button
                          className="icon-button"
                          title="下移"
                          disabled={
                            index === plan.items.length - 1 || Boolean(saving)
                          }
                          onClick={() => moveItem(index, 1)}
                        >
                          <ArrowDown size={16} />
                        </button>
                        <button
                          className="icon-button danger-action"
                          title="从今日待办移除"
                          disabled={Boolean(saving)}
                          onClick={() => removeItem(index)}
                        >
                          <Trash2 size={16} />
                        </button>
                      </>
                    )}
                  </div>
                </article>
              ))}
            </div>
          ) : (
            <EmptyState
              icon={CalendarCheck2}
              title="今天还没有明确重点"
              body="先生成系统建议，或从任务库手工挑选。清单确认前都只是草案。"
              action={
                can(auth, "today", "create") && (
                  <button className="primary-button" onClick={recommend}>
                    <Sparkles size={16} /> 生成今日建议
                  </button>
                )
              }
            />
          )}
        </section>

        <aside className="panel today-side-panel">
          <div className="panel-heading">
            <div>
              <span className="eyebrow">推荐逻辑</span>
              <h3>为什么是这些</h3>
            </div>
            <Target size={18} />
          </div>
          <div className="recommendation-note">
            <p>优先考虑李超本人负责、P0/P1、正在推进、逾期或临近截止且下一动作明确的事项。</p>
            <p>阻塞、等待、已完成和已取消任务不会进入自动推荐。</p>
          </div>
          {can(auth, "today", "edit") && (
            <div className="today-add-task">
              <label>
                <span>从任务库补充</span>
                <select
                  value={selectedTask}
                  onChange={(event) => setSelectedTask(event.target.value)}
                >
                  <option value="">选择一条未完成任务</option>
                  {availableTasks.map((task) => (
                    <option key={task.id} value={task.id}>
                      {task.priority} · {task.title}
                    </option>
                  ))}
                </select>
              </label>
              <button
                className="secondary-button"
                disabled={!selectedTask || Boolean(saving)}
                onClick={addTask}
              >
                <Plus size={16} /> 加入
              </button>
            </div>
          )}
          <div className="suggestion-list">
            <strong>其他候选</strong>
            {plan.suggestions.slice(0, 4).map((task) => (
              <button key={task.id} onClick={() => onOpen("task", task.id)}>
                <span>{task.priority}</span>
                <div>
                  <strong>{task.title}</strong>
                  <small>{task.recommendation_reason}</small>
                </div>
              </button>
            ))}
          </div>
        </aside>
      </div>
    </>
  );
}

function TaskExecutionProgress({ task }) {
  if (task.progress_percent !== null && task.progress_percent !== undefined) {
    return (
      <div className="execution-progress">
        <ProgressBar
          value={task.progress_percent}
          manual={task.progress_is_manual}
          compact
        />
        <small>{task.progress_current || "进度说明待更新"}</small>
      </div>
    );
  }

  return (
    <div className="execution-progress status-progress">
      <Badge>{task.status}</Badge>
      <small>{task.progress_current || "具体进度待更新"}</small>
    </div>
  );
}

function ExecutionGoalRow({ goal }) {
  return (
    <div className="execution-goal-row">
      <div className="execution-goal-name">
        <Target size={17} />
        <span>
          <small className="execution-goal-kind">目标 · {goal.name}</small>
          <strong className="execution-goal-result">{goal.result}</strong>
          {goal.next_milestone && goal.next_milestone !== goal.result && (
            <small className="execution-goal-next">下一节点：{goal.next_milestone}</small>
          )}
        </span>
      </div>
      <div className="execution-goal-owner">
        <span>负责人</span>
        <strong>{goal.owner_name || "待确认"}</strong>
      </div>
      <div className="execution-goal-status">
        <span>目标状态</span>
        <Badge>{goal.status}</Badge>
      </div>
      <div className="execution-goal-load">
        <span>待完成工作</span>
        <strong>{goal.metrics.active} 项</strong>
        {(goal.metrics.overdue > 0 || goal.metrics.blocked > 0) && (
          <small>
            {goal.metrics.overdue > 0 ? `${goal.metrics.overdue} 逾期` : ""}
            {goal.metrics.overdue > 0 && goal.metrics.blocked > 0 ? " · " : ""}
            {goal.metrics.blocked > 0 ? `${goal.metrics.blocked} 阻塞` : ""}
          </small>
        )}
      </div>
      <div className="execution-goal-evidence">
        <span>验收</span>
        <strong className={goal.evidence ? "" : "pending-copy"}>
          {goal.evidence || "证据待补充"}
        </strong>
      </div>
    </div>
  );
}

function WorkBlocksView({ data, onOpen, onEdit, onNew, onArchive, auth }) {
  const [showArchived, setShowArchived] = useState(false);
  const [expandedBlocks, setExpandedBlocks] = useState(() => new Set());
  const blocks = useMemo(
    () => buildWorkBlockExecution(data, showArchived),
    [data, showArchived],
  );
  const priorityBlocks = useMemo(() => getPriorityWorkBlocks(blocks), [blocks]);
  const activeTaskCount = blocks.reduce(
    (total, block) => total + block.executionTasks.length,
    0,
  );

  const toggleBlockTasks = (blockId) => {
    setExpandedBlocks((current) => {
      const next = new Set(current);
      if (next.has(blockId)) next.delete(blockId);
      else next.add(blockId);
      return next;
    });
  };

  return (
    <>
      <SectionHeader
        title="工作块"
        description="从最终成果倒推目标、负责人和待完成工作；先看结果，再看动作。"
        action={
          <div className="header-actions">
            <button className="secondary-button" onClick={() => setShowArchived(!showArchived)}>
              {showArchived ? <ArchiveRestore size={16} /> : <Archive size={16} />}
              {showArchived ? "返回进行中" : "查看归档"}
            </button>
            {can(auth, "work-blocks", "create") && (
              <button className="primary-button" onClick={onNew}>
                <Plus size={16} /> 新增工作块
              </button>
            )}
          </div>
        }
      />

      {!showArchived && (
        <section className="panel execution-system-strip" aria-label="七个习惯执行规则">
          <div className="execution-system-title">
            <span className="eyebrow">七个习惯 · 执行秩序</span>
            <strong>先确定成果，再分配责任，最后检查证据</strong>
          </div>
          <div className="habit-steps">
            <span><b>1</b>主动选择</span>
            <span><b>2</b>以终为始</span>
            <span><b>3</b>要事第一</span>
            <span><b>4</b>明确双赢</span>
            <span><b>5</b>先理解</span>
            <span><b>6</b>协同增效</span>
            <span><b>7</b>每周复盘</span>
          </div>
        </section>
      )}

      {!showArchived && blocks.length > 0 && (
        <section className="panel priority-work-strip">
          <div>
            <span className="eyebrow">要事第一</span>
            <strong>当前优先工作块</strong>
            <small>按未完成 P0 自动汇总，最终“大石头”由李超确认。</small>
          </div>
          <div className="priority-work-list">
            {priorityBlocks.length ? priorityBlocks.map((block) => (
              <button key={block.id} onClick={() => onOpen("work-block", block.id)}>
                <span>{block.name}</span>
                <Badge className="priority-p0">P0 · {block.activeP0}</Badge>
              </button>
            )) : <span className="priority-empty">暂无已确认 P0，请先确认本期大石头。</span>}
          </div>
          <div className="execution-total">
            <strong>{activeTaskCount}</strong>
            <span>项待完成工作</span>
          </div>
        </section>
      )}

      {blocks.length ? (
        <div className="work-block-execution-list">
          {blocks.map((block) => {
            const expanded = expandedBlocks.has(block.id);
            const visibleTasks = expanded
              ? block.executionTasks
              : block.executionTasks.slice(0, 6);
            const remaining = block.executionTasks.length - visibleTasks.length;
            return (
              <section className="panel work-block-execution" key={block.id}>
                <header className="work-block-execution-header">
                  <button
                    className="execution-block-title"
                    onClick={() => onOpen("work-block", block.id)}
                  >
                    <span className="block-index">{block.displayIndex}</span>
                    <span>
                      <span className="execution-title-line">
                        <strong>{block.name}</strong>
                        {block.activeP0 > 0 && <Badge className="priority-p0">P0 · {block.activeP0}</Badge>}
                      </span>
                      <small>{block.current_focus || "当前重点待补充"}</small>
                    </span>
                  </button>
                  <div className="execution-block-owner">
                    <span>工作块负责人</span>
                    <WorkBlockOwners block={block} />
                  </div>
                  <div className="execution-block-health">
                    <span>健康状态</span>
                    <Badge>{block.health}</Badge>
                  </div>
                  <div className="execution-block-metrics">
                    <span><strong>{block.executionTasks.length}</strong> 待完成</span>
                    <span className={block.metrics.overdue ? "danger-text" : ""}><strong>{block.metrics.overdue}</strong> 逾期</span>
                    <span className={block.metrics.blocked ? "danger-text" : ""}><strong>{block.metrics.blocked}</strong> 阻塞</span>
                  </div>
                  <div className="row-actions">
                    {can(auth, "work-blocks", "edit") && (
                      <button className="icon-button" title="编辑工作块" onClick={() => onEdit(block)}><Edit3 size={16} /></button>
                    )}
                    {can(auth, "work-blocks", "operate") && (
                      <button
                        className="icon-button"
                        title={block.archived ? "恢复工作块" : "归档工作块"}
                        onClick={() => onArchive(block)}
                      >
                        {block.archived ? <ArchiveRestore size={16} /> : <Archive size={16} />}
                      </button>
                    )}
                  </div>
                </header>

                <div className="execution-outcome-path">
                  <article>
                    <span>最终目标 · 以终为始</span>
                    <p>{block.description || "最终成果与量化口径待确认"}</p>
                  </article>
                  <article className="execution-month-goal">
                    <span>{monthGoalLabel(block.month_goal_period)}</span>
                    <p className="preline">{block.month_goal || "本月末应形成的可验收成果待确认"}</p>
                  </article>
                  <article>
                    <span>本周目标 · 要事第一</span>
                    <p className="preline">{block.week_goal || "本周应交付成果待确认"}</p>
                  </article>
                  <article>
                    <span>下一验收节点</span>
                    <p>{block.next_milestone || "下一节点待确认"}</p>
                    <small>截止：{formatDate(block.due_date)}</small>
                  </article>
                </div>

                {(block.blockers_list.length > 0 || block.decisions.length > 0) && (
                  <div className="execution-alerts">
                    {block.blockers_list.length > 0 && (
                      <div><AlertCircle size={16} /><span><strong>当前卡点</strong>{block.blockers_list.join("；")}</span></div>
                    )}
                    {block.decisions.length > 0 && (
                      <div><CircleHelp size={16} /><span><strong>待李超拍板</strong>{block.decisions.join("；")}</span></div>
                    )}
                  </div>
                )}

                <div className="execution-subsection">
                  <div className="execution-subsection-heading">
                    <div>
                      <span className="eyebrow">目标责任</span>
                      <h3>目标与负责人</h3>
                    </div>
                    <small>目标必须有负责人、工作量和验收证据</small>
                  </div>
                  <div className="execution-goal-list">
                    {block.executionGoals.map((goal) => (
                      <ExecutionGoalRow key={goal.id} goal={goal} />
                    ))}
                  </div>
                </div>

                <div className="execution-subsection execution-task-section">
                  <div className="execution-subsection-heading">
                    <div>
                      <span className="eyebrow">执行清单</span>
                      <h3>待完成工作</h3>
                    </div>
                    <small>P0/P1 优先；沟通、跟进和测试只有形成可验收结果才算完成</small>
                  </div>
                  {visibleTasks.length ? (
                    <div className="execution-task-list">
                      <div className="execution-task-columns" aria-hidden="true">
                        <span>工作</span><span>负责人</span><span>状态与进度</span><span>截止 / 下一动作</span><span>验收</span>
                      </div>
                      {visibleTasks.map((task) => (
                        <div
                          className={`execution-task-row ${task.overdue ? "is-overdue" : ""}`}
                          key={task.id}
                        >
                          <div className="execution-task-main">
                            <span className={`priority ${priorityClass[task.priority]}`}>{task.priority}</span>
                            <span>
                              <TaskTitle task={task} onOpen={onOpen} />
                              <small>{task.project_name || "直属工作"}</small>
                            </span>
                          </div>
                          <div className="execution-task-owner">
                            <span className="mobile-field-label">负责人</span>
                            <strong>{task.assignee_name || "待确认"}</strong>
                            {task.collaborators && <small>协作：{task.collaborators}</small>}
                          </div>
                          <div>
                            <span className="mobile-field-label">状态与进度</span>
                            <TaskExecutionProgress task={task} />
                          </div>
                          <div className="execution-task-next">
                            <span className="mobile-field-label">截止 / 下一动作</span>
                            <strong className={task.overdue ? "danger-text" : ""}>
                              {task.overdue ? "逾期 · " : ""}{formatDate(task.due_date)}
                            </strong>
                            <small>{task.next_action || "下一动作待更新"}</small>
                            {task.blocker && <small className="danger-text">卡点：{task.blocker}</small>}
                          </div>
                          <div className="execution-task-evidence">
                            <span className="mobile-field-label">验收</span>
                            {task.evidence ? <><FileCheck2 size={16} /><span>{task.evidence}</span></> : <span className="pending-copy">证据待补充</span>}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <EmptyState
                      icon={CheckCircle2}
                      title="暂无待完成工作"
                      body="该工作块当前没有未完成任务。"
                    />
                  )}
                  {block.executionTasks.length > 6 && (
                    <button
                      className={`execution-expand ${expanded ? "expanded" : ""}`}
                      onClick={() => toggleBlockTasks(block.id)}
                    >
                      <ChevronDown size={17} />
                      {expanded ? "收起工作" : `展开其余 ${remaining} 项工作`}
                    </button>
                  )}
                </div>
              </section>
            );
          })}
        </div>
      ) : (
        <section className="panel section-panel">
          <EmptyState
            icon={Archive}
            title={showArchived ? "暂无归档工作块" : "暂无工作块"}
            body={showArchived ? "归档后的工作块会保留全部任务与历史。" : "新增第一个工作块开始管理。"}
          />
        </section>
      )}
    </>
  );
}

function PeopleView({ data, onOpen }) {
  const activePeople = data.people
    .filter((person) => person.metrics.open > 0 || person.work_blocks.length > 0)
    .sort((a, b) => b.metrics.score - a.metrics.score);
  return (
    <>
      <SectionHeader
        title="人员"
        description="按人汇总跨工作块任务、逾期、卡点与可解释负载。MVP 不虚构预计工时。"
      />
      <section className="panel section-panel">
        <div className="table-scroll">
          <table className="data-table people-table">
            <thead>
              <tr>
                <th>人员</th>
                <th>角色</th>
                <th>工作块</th>
                <th>未完成</th>
                <th>逾期</th>
                <th>阻塞</th>
                <th>本周目标</th>
                <th>负载</th>
              </tr>
            </thead>
            <tbody>
              {activePeople.map((person) => (
                <tr key={person.id} onClick={() => onOpen("person", person.id)} className="clickable-row">
                  <td>
                    <span className="person-cell strong">
                      <Avatar name={person.name} />
                      <span><strong>{person.name}</strong><small>最近进度：{person.recent_progress}</small></span>
                    </span>
                  </td>
                  <td>{person.role || "待确认"}</td>
                  <td>
                    <div className="tag-list">
                      {person.work_blocks.slice(0, 3).map((name) => <span key={name}>{name}</span>)}
                      {person.work_blocks.length > 3 && <span>+{person.work_blocks.length - 3}</span>}
                    </div>
                  </td>
                  <td>{person.metrics.open}</td>
                  <td className={person.metrics.overdue ? "danger-text" : ""}>{person.metrics.overdue}</td>
                  <td className={person.metrics.blocked ? "danger-text" : ""}>{person.metrics.blocked}</td>
                  <td><p className="clamp-2">{person.week_goal}</p></td>
                  <td><Badge tone={person.workload === "高" ? "red" : person.workload === "中" ? "amber" : "green"}>{person.workload}</Badge></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </>
  );
}

function TasksView({ data, globalSearch, onOpen, onEdit, onAction, onNew, auth }) {
  const [query, setQuery] = useState(globalSearch || "");
  const [status, setStatus] = useState("全部");
  const [priority, setPriority] = useState("全部");
  const [block, setBlock] = useState("全部");
  const [owner, setOwner] = useState("全部");
  const [sort, setSort] = useState("优先级");

  useEffect(() => setQuery(globalSearch || ""), [globalSearch]);

  const tasks = useMemo(() => {
    const priorityOrder = { P0: 0, P1: 1, P2: 2, P3: 3, 待确认: 4 };
    return data.tasks
      .filter((task) => {
        const haystack = [
          task.title,
          task.work_block_name,
          task.assignee_name,
          task.blocker,
          task.next_action,
        ].join(" ");
        return (
          (!query || haystack.toLowerCase().includes(query.toLowerCase())) &&
          (status === "全部" || task.status === status) &&
          (priority === "全部" || task.priority === priority) &&
          (block === "全部" || task.work_block_id === block) &&
          (owner === "全部" || task.assignee_id === owner)
        );
      })
      .sort((a, b) => {
        if (sort === "截止时间") return String(a.due_date || "9999").localeCompare(String(b.due_date || "9999"));
        if (sort === "最近更新") return String(b.updated_at).localeCompare(String(a.updated_at));
        return (priorityOrder[a.priority] ?? 9) - (priorityOrder[b.priority] ?? 9);
      });
  }, [data, query, status, priority, block, owner, sort]);

  return (
    <>
      <SectionHeader
        title="任务"
        description={`共 ${data.tasks.length} 条历史与当前任务；完成和取消只改变状态，不删除记录。`}
        action={can(auth, "tasks", "create") && (
          <button className="primary-button" onClick={onNew}><Plus size={16} /> 快速新增</button>
        )}
      />
      <section className="panel filter-panel">
        <label className="inline-search">
          <Search size={16} />
          <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="搜索任务、卡点、人员…" />
        </label>
        <div className="filter-fields">
          <label><span>状态</span><select value={status} onChange={(e) => setStatus(e.target.value)}>{["全部", "未开始", "进行中", "阻塞", "等待", "已完成", "已取消"].map((v) => <option key={v}>{v}</option>)}</select></label>
          <label><span>优先级</span><select value={priority} onChange={(e) => setPriority(e.target.value)}>{["全部", "P0", "P1", "P2", "P3", "待确认"].map((v) => <option key={v}>{v}</option>)}</select></label>
          <label><span>工作块</span><select value={block} onChange={(e) => setBlock(e.target.value)}><option value="全部">全部</option>{data.work_blocks.filter((b) => !b.archived).map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}</select></label>
          <label><span>负责人</span><select value={owner} onChange={(e) => setOwner(e.target.value)}><option value="全部">全部</option>{data.people.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}</select></label>
          <label><span>排序</span><select value={sort} onChange={(e) => setSort(e.target.value)}>{["优先级", "截止时间", "最近更新"].map((v) => <option key={v}>{v}</option>)}</select></label>
        </div>
        <span className="filter-result"><Filter size={14} /> {tasks.length} 条结果</span>
      </section>
      <section className="panel section-panel">
        <TaskTable
          tasks={tasks}
          onOpen={onOpen}
          onEdit={onEdit}
          onAction={onAction}
          permissions={auth.permissions.tasks}
        />
      </section>
    </>
  );
}

function EventsView({ data }) {
  return (
    <>
      <SectionHeader
        title="更新记录"
        description="界面、CLI、导入和备份都记录来源与修改前后，便于追溯。"
      />
      <section className="panel timeline-panel">
        <div className="timeline">
          {data.events.map((event) => (
            <div className="timeline-item" key={event.id}>
              <span className="timeline-dot" />
              <div className="timeline-meta">
                <strong>{event.action}</strong>
                <span>{event.actor} · {event.source} · {formatDate(event.created_at)}</span>
              </div>
              <div className="timeline-copy">
                {event.after?.title || event.after?.name || event.note || (
                  event.after?.file_path ? "生成了一份可恢复或写回的文件" : "系统状态已更新"
                )}
              </div>
              <Badge tone={event.source.includes("导入") ? "blue" : "neutral"}>{event.entity_type}</Badge>
            </div>
          ))}
        </div>
      </section>
    </>
  );
}

function AdminView({ auth, onToast }) {
  const [accessData, setAccessData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState("");

  const loadUsers = useCallback(async () => {
    setLoading(true);
    try {
      const result = await api("/api/admin/users");
      setAccessData(result.data);
      setError("");
    } catch (loadError) {
      setError(loadError.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadUsers(); }, [loadUsers]);

  function updateStatus(userId, status) {
    setAccessData((current) => ({
      ...current,
      users: current.users.map((user) =>
        user.id === userId ? { ...user, status } : user
      ),
    }));
  }

  function updatePermission(userId, pageId, actionId, checked) {
    setAccessData((current) => ({
      ...current,
      users: current.users.map((user) => {
        if (user.id !== userId) return user;
        const page = { ...(user.permissions[pageId] || {}) };
        page[actionId] = checked;
        if (actionId !== "view" && checked) page.view = true;
        if (actionId === "view" && !checked) {
          page.create = false;
          page.edit = false;
          page.operate = false;
        }
        return {
          ...user,
          permissions: { ...user.permissions, [pageId]: page },
        };
      }),
    }));
  }

  async function save(user) {
    setSaving(user.id);
    try {
      await api(`/api/admin/users/${user.id}/permissions`, {
        method: "PUT",
        body: JSON.stringify({
          status: user.status,
          permissions: user.permissions,
        }),
      });
      onToast(`${user.name}的访问权限已保存`);
      await loadUsers();
    } catch (saveError) {
      onToast(saveError.message);
    } finally {
      setSaving("");
    }
  }

  return (
    <>
      <SectionHeader
        title="用户与权限"
        description="新飞书用户默认没有任何页面和操作权限。管理员授权后立即生效。"
        action={
          <button className="secondary-button" onClick={loadUsers}>
            <RefreshCw size={16} /> 刷新用户
          </button>
        }
      />
      {loading && (
        <section className="panel admin-loading">正在读取飞书用户…</section>
      )}
      {error && (
        <section className="panel form-error"><AlertCircle size={16} />{error}</section>
      )}
      {!loading && !error && (
        <section className="panel admin-panel">
          <div className="admin-summary">
            <span><strong>{accessData.users.length}</strong> 个登录用户</span>
            <span><strong>{accessData.users.filter((user) => user.status === "pending").length}</strong> 个待授权</span>
            <span><strong>{accessData.users.filter((user) => user.is_admin).length}</strong> 个管理员</span>
          </div>
          <div className="access-user-list">
            {accessData.users.map((user) => (
              <article className="access-user" key={user.id}>
                <div className="access-user-heading">
                  <Avatar name={user.name} />
                  <div>
                    <strong>{user.name}</strong>
                    <small>{user.open_id}</small>
                  </div>
                  {user.is_admin ? (
                    <Badge tone="green">管理员</Badge>
                  ) : (
                    <select
                      aria-label={`${user.name}账号状态`}
                      value={user.status}
                      onChange={(event) => updateStatus(user.id, event.target.value)}
                    >
                      <option value="pending">待授权</option>
                      <option value="active">已启用</option>
                      <option value="disabled">已停用</option>
                    </select>
                  )}
                </div>
                <div className="table-scroll">
                  <table className="permission-table">
                    <thead>
                      <tr>
                        <th>页面</th>
                        <th>查看</th>
                        <th>新增</th>
                        <th>编辑</th>
                        <th>其他操作</th>
                      </tr>
                    </thead>
                    <tbody>
                      {accessData.pages.map((page) => {
                        const allowedActions = new Set(page.actions.map((action) => action.id));
                        return (
                          <tr key={page.id}>
                            <td>
                              <strong>{page.label}</strong>
                              <small>
                                {page.actions.map((action) => action.label).join(" · ")}
                              </small>
                            </td>
                            {["view", "create", "edit", "operate"].map((actionId) => (
                              <td key={actionId}>
                                {allowedActions.has(actionId) ? (
                                  <input
                                    type="checkbox"
                                    aria-label={`${user.name}-${page.label}-${actionId}`}
                                    checked={user.is_admin || Boolean(user.permissions[page.id]?.[actionId])}
                                    disabled={user.is_admin}
                                    onChange={(event) =>
                                      updatePermission(user.id, page.id, actionId, event.target.checked)
                                    }
                                  />
                                ) : (
                                  <span className="not-applicable">—</span>
                                )}
                              </td>
                            ))}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <div className="access-user-footer">
                  <span>
                    {user.last_login_at
                      ? `最近登录：${formatDate(user.last_login_at)}`
                      : "尚未完成首次登录"}
                  </span>
                  {!user.is_admin && (
                    <button
                      className="primary-button"
                      disabled={saving === user.id}
                      onClick={() => save(user)}
                    >
                      {saving === user.id
                        ? <RefreshCw className="spin" size={16} />
                        : <Save size={16} />}
                      保存权限
                    </button>
                  )}
                </div>
              </article>
            ))}
          </div>
        </section>
      )}
    </>
  );
}

function LoginScreen({ configured, error }) {
  const returnTo = currentReturnTo(window.location);
  return (
    <div className="auth-screen">
      <section className="auth-panel">
        <span className="brand-mark large">工</span>
        <span className="eyebrow">工作块与人员任务管理</span>
        <h1>登录工作台</h1>
        <p>使用飞书身份登录。首次登录不会自动获得数据访问权限，需要管理员授权。</p>
        {error && <div className="auth-error"><AlertCircle size={17} />{error}</div>}
        {configured ? (
          <a className="feishu-login-button" href={`/api/auth/feishu/start?return_to=${encodeURIComponent(returnTo)}`}>
            <LogIn size={19} /> 使用飞书登录
          </a>
        ) : (
          <div className="auth-error"><AlertCircle size={17} />飞书登录尚未配置完成</div>
        )}
        <small>登录只用于身份识别；具体页面和操作权限由系统管理员分配。</small>
      </section>
    </div>
  );
}

function PendingAccess({ user, onLogout }) {
  return (
    <div className="auth-screen">
      <section className="auth-panel">
        <Avatar name={user.name} size="lg" />
        <span className="eyebrow">已通过飞书识别</span>
        <h1>{user.name}，等待管理员授权</h1>
        <p>你的账号已经进入用户列表，但目前没有任何页面或操作权限。</p>
        <div className="pending-open-id">
          <span>飞书 Open ID</span>
          <strong>{user.open_id}</strong>
        </div>
        <button className="secondary-button" onClick={onLogout}>
          <LogOut size={17} /> 退出登录
        </button>
      </section>
    </div>
  );
}

function DetailDrawer({ drawer, data, onClose, onEditTask, onEditBlock, onAction, auth }) {
  if (!drawer) return null;
  const item =
    drawer.type === "work-block"
      ? data.work_blocks.find((block) => block.id === drawer.id)
      : drawer.type === "person"
        ? data.people.find((person) => person.id === drawer.id)
        : data.tasks.find((task) => task.id === drawer.id);
  if (!item) return null;

  const blockTasks = drawer.type === "work-block" ? data.tasks.filter((task) => task.work_block_id === item.id) : [];
  const blockProjects = drawer.type === "work-block" ? data.projects.filter((project) => project.work_block_id === item.id) : [];
  const history = data.events.filter((event) => event.entity_id === item.id).slice(0, 12);

  return (
    <div className="drawer-layer" role="dialog" aria-modal="true">
      <button className="drawer-backdrop" aria-label="关闭详情" onClick={onClose} />
      <aside className="detail-drawer">
        <div className="drawer-header">
          <div>
            <span className="eyebrow">{drawer.type === "work-block" ? "工作块详情" : drawer.type === "person" ? "人员详情" : "任务详情"}</span>
            <h2>{item.name || item.title}</h2>
          </div>
          <button className="icon-button" onClick={onClose} aria-label="关闭"><X size={20} /></button>
        </div>

        {drawer.type === "work-block" && (
          <div className="drawer-content">
            <div className="drawer-actions">
              <Badge>{item.health}</Badge>
              {can(auth, "work-blocks", "edit") && (
                <button className="secondary-button small" onClick={() => onEditBlock(item)}><Edit3 size={15} /> 编辑</button>
              )}
            </div>
            <div className="detail-progress">
              <ProgressBar value={item.progress} manual={item.progress_is_manual} />
              <div className="fact-metrics horizontal">
                <span>{item.metrics.milestones_completed}/{item.metrics.milestones_total} 里程碑</span>
                <span>{item.metrics.overdue} 逾期</span>
                <span>{item.metrics.blocked} 阻塞</span>
                <span>更新 {formatDate(item.updated_at)}</span>
              </div>
            </div>
            <div className="detail-grid">
              <article><span>负责人</span><strong>{item.owner_name || "待确认"}</strong></article>
              <article><span>截止时间</span><strong>{formatDate(item.due_date)}</strong></article>
              <article className="wide"><span>目标说明</span><p>{item.description || "待补充"}</p></article>
              <article className="wide"><span>{monthGoalLabel(item.month_goal_period, false)}</span><p className="preline">{item.month_goal || "待确认"}</p></article>
              <article className="wide"><span>本周目标</span><p className="preline">{item.week_goal || "待确认"}</p></article>
              <article className="wide"><span>当前重点</span><p className="preline">{item.current_focus || "待补充"}</p></article>
              <article className="wide"><span>下一节点</span><p>{item.next_milestone || "待确认"}</p></article>
              <article className="wide danger-article"><span>卡点</span><p>{item.blockers || "暂无明确卡点"}</p></article>
              <article className="wide"><span>需要李超拍板</span><p>{item.decisions || "暂无"}</p></article>
              <article className="wide"><span>验收证据</span><p>{item.evidence || "待补充"}</p></article>
            </div>
            <div className="drawer-section">
              <h3>项目</h3>
              {blockProjects.length ? blockProjects.map((project) => (
                <div className="project-line" key={project.id}><CircleDot size={15} /><span><strong>{project.name}</strong><small>{project.status} · {project.owner_name || "负责人待确认"}</small></span></div>
              )) : <p className="muted-text">当前任务直接归属于工作块。</p>}
            </div>
            <div className="drawer-section">
              <h3>任务与里程碑</h3>
              <TaskTable compact tasks={blockTasks.slice(0, 20)} onOpen={() => {}} onEdit={onEditTask} onAction={onAction} permissions={auth.permissions.tasks} />
            </div>
            <div className="drawer-section">
              <h3>周复盘与时间线</h3>
              {history.length ? history.map((event) => <div className="history-line" key={event.id}><span>{formatDate(event.created_at)}</span><strong>{event.action}</strong><small>{event.actor}</small></div>) : <p className="muted-text">初始状态来自正式资料，后续修改会在这里形成时间线。</p>}
            </div>
          </div>
        )}

        {drawer.type === "person" && (
          <div className="drawer-content">
            <div className="person-hero">
              <Avatar name={item.name} size="lg" />
              <div><h3>{item.name}</h3><p>{item.role || "角色待确认"}</p></div>
              <Badge tone={item.workload === "高" ? "red" : item.workload === "中" ? "amber" : "green"}>负载 {item.workload}</Badge>
            </div>
            <div className="mini-metrics">
              <span><strong>{item.metrics.open}</strong>进行中</span>
              <span><strong>{item.metrics.overdue}</strong>逾期</span>
              <span><strong>{item.metrics.blocked}</strong>阻塞</span>
              <span><strong>{item.metrics.waiting}</strong>等待</span>
            </div>
            <div className="detail-grid">
              <article className="wide"><span>本周目标</span><p>{item.week_goal}</p></article>
              <article className="wide"><span>最近进度</span><p>{item.recent_progress}</p></article>
              <article className="wide"><span>跨工作块分布</span><div className="tag-list">{item.work_blocks.map((name) => <span key={name}>{name}</span>)}</div></article>
            </div>
            <div className="drawer-section">
              <h3>全部任务</h3>
              <TaskTable compact tasks={item.tasks} onOpen={() => {}} onEdit={onEditTask} onAction={onAction} permissions={auth.permissions.tasks} />
            </div>
          </div>
        )}

        {drawer.type === "task" && (
          <div className="drawer-content">
            <div className="drawer-actions">
              <div className="status-stack"><span className={`priority ${priorityClass[item.priority]}`}>{item.priority}</span><Badge>{item.status}</Badge>{item.overdue && <Badge tone="red">已逾期</Badge>}</div>
              {can(auth, "tasks", "edit") && (
                <button className="secondary-button small" onClick={() => onEditTask(item)}><Edit3 size={15} /> 编辑</button>
              )}
            </div>
            <div className="detail-grid">
              <article><span>工作块</span><strong>{item.work_block_name || "待归类"}</strong></article>
              <article><span>项目</span><strong>{item.project_name || "直接属于工作块"}</strong></article>
              <article><span>负责人</span><strong>{item.assignee_name || "待确认"}</strong></article>
              <article><span>协作人</span><strong>{item.collaborators || "待补充"}</strong></article>
              <article><span>开始时间</span><strong>{formatDate(item.start_date)}</strong></article>
              <article><span>截止时间</span><strong>{formatDate(item.due_date)}</strong></article>
              <article className="wide"><span>本周目标</span><p>{item.week_goal || "待确认"}</p></article>
              <article className="wide"><span>当前进度</span><p>{item.progress_current || "待补充"}</p>{item.progress_percent !== null && <ProgressBar value={item.progress_percent} manual={item.progress_is_manual} />}</article>
              <article className="wide danger-article"><span>卡点</span><p>{item.blocker || "暂无明确卡点"}</p></article>
              <article className="wide"><span>下一动作</span><p>{item.next_action || "待补充"}</p></article>
              <article className="wide"><span>等待他人</span><p>{item.waiting_on || "无"}</p></article>
              <article className="wide"><span>验收证据</span><p>{item.evidence || "待补充"}</p></article>
              <article className="wide source-article"><span>来源</span><p>{item.source_document || item.source || "界面"}</p><small>{item.source_section}</small></article>
            </div>
            {can(auth, "tasks", "operate") && (
              <div className="drawer-quick-actions">
                <button onClick={() => onAction(item, item.status === "已完成" ? "reopen" : "complete")}><Check size={16} />{item.status === "已完成" ? "重新打开" : "完成"}</button>
                <button onClick={() => onAction(item, "block")}><ShieldAlert size={16} />阻塞</button>
                <button onClick={() => onAction(item, "transfer")}><UserRound size={16} />转交</button>
                <button onClick={() => onAction(item, "postpone")}><CalendarClock size={16} />延期</button>
                <button className="danger-action" onClick={() => onAction(item, "cancel")}><Ban size={16} />取消</button>
              </div>
            )}
            <div className="drawer-section">
              <h3>修改历史</h3>
              {history.length ? history.map((event) => <div className="history-line" key={event.id}><span>{formatDate(event.created_at)}</span><strong>{event.action}</strong><small>{event.actor} · {event.source}</small></div>) : <p className="muted-text">这条任务来自初始导入，暂无后续修改。</p>}
            </div>
          </div>
        )}
      </aside>
    </div>
  );
}

function FormModal({ modal, data, onClose, onSaved }) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  if (!modal) return null;
  const isTask = modal.type === "task";
  const item = modal.item || {};

  async function submit(event) {
    event.preventDefault();
    setSaving(true);
    setError("");
    const form = new FormData(event.currentTarget);
    const payload = Object.fromEntries(form.entries());
    Object.keys(payload).forEach((key) => {
      if (payload[key] === "") payload[key] = null;
    });
    try {
      if (isTask) {
        await api(item.id ? `/api/tasks/${item.id}` : "/api/tasks", {
          method: item.id ? "PATCH" : "POST",
          body: JSON.stringify(payload),
        });
      } else {
        await api(item.id ? `/api/work-blocks/${item.id}` : "/api/work-blocks", {
          method: item.id ? "PATCH" : "POST",
          body: JSON.stringify(payload),
        });
      }
      await onSaved(`${isTask ? "任务" : "工作块"}已${item.id ? "更新" : "新增"}`);
      onClose();
    } catch (submitError) {
      setError(submitError.message);
    } finally {
      setSaving(false);
    }
  }

  const projects = data.projects.filter((project) => !item.work_block_id || project.work_block_id === item.work_block_id);

  return (
    <div className="modal-layer" role="dialog" aria-modal="true">
      <button className="modal-backdrop" aria-label="关闭表单" onClick={onClose} />
      <form className="form-modal" onSubmit={submit}>
        <div className="modal-header">
          <div><span className="eyebrow">{item.id ? "编辑" : "新增"}</span><h2>{isTask ? "任务" : "工作块"}</h2></div>
          <button type="button" className="icon-button" onClick={onClose}><X size={20} /></button>
        </div>
        <div className="form-body">
          {error && <div className="form-error"><AlertCircle size={16} />{error}</div>}
          {isTask ? (
            <>
              <label className="field wide"><span>任务标题 *</span><input name="title" defaultValue={item.title || ""} required autoFocus /></label>
              <label className="field"><span>所属工作块</span><select name="work_block_id" defaultValue={item.work_block_id || ""}><option value="">待归类</option>{data.work_blocks.filter((b) => !b.archived).map((block) => <option key={block.id} value={block.id}>{block.name}</option>)}</select></label>
              <label className="field"><span>项目（可选）</span><select name="project_id" defaultValue={item.project_id || ""}><option value="">直接属于工作块</option>{projects.map((project) => <option key={project.id} value={project.id}>{project.name}</option>)}</select></label>
              <label className="field"><span>实际负责人</span><select name="assignee_id" defaultValue={item.assignee_id || ""}><option value="">待确认</option>{data.people.map((person) => <option key={person.id} value={person.id}>{person.name}</option>)}</select></label>
              <label className="field"><span>协作人</span><input name="collaborators" defaultValue={item.collaborators || ""} placeholder="多人用顿号分隔" /></label>
              <label className="field"><span>优先级</span><select name="priority" defaultValue={item.priority || "待确认"}>{["P0", "P1", "P2", "P3", "待确认"].map((value) => <option key={value}>{value}</option>)}</select></label>
              <label className="field"><span>状态</span><select name="status" defaultValue={item.status || "未开始"}>{["未开始", "进行中", "阻塞", "等待", "已完成", "已取消"].map((value) => <option key={value}>{value}</option>)}</select></label>
              <label className="field"><span>开始时间</span><input name="start_date" type="date" defaultValue={item.start_date || ""} /></label>
              <label className="field"><span>截止时间</span><input name="due_date" type="date" defaultValue={item.due_date || ""} /></label>
              <label className="field wide"><span>本周目标</span><textarea name="week_goal" defaultValue={item.week_goal || ""} rows="2" /></label>
              <label className="field wide"><span>当前进度</span><textarea name="progress_current" defaultValue={item.progress_current || ""} rows="2" /></label>
              <label className="field"><span>手动进度 %</span><input name="progress_percent" type="number" min="0" max="100" defaultValue={item.progress_percent ?? ""} placeholder="留空则不使用手动值" /></label>
              <label className="field"><span>等待他人事项</span><input name="waiting_on" defaultValue={item.waiting_on || ""} /></label>
              <label className="field wide"><span>卡点</span><textarea name="blocker" defaultValue={item.blocker || ""} rows="2" /></label>
              <label className="field wide"><span>下一动作</span><textarea name="next_action" defaultValue={item.next_action || ""} rows="2" /></label>
              <label className="field wide"><span>验收证据</span><textarea name="evidence" defaultValue={item.evidence || ""} rows="2" /></label>
              <label className="field"><span>确认状态</span><select name="confirmation_status" defaultValue={item.confirmation_status || "待确认"}><option>待确认</option><option>已记录</option><option>已确认</option></select></label>
            </>
          ) : (
            <>
              <label className="field wide"><span>工作块名称 *</span><input name="name" defaultValue={item.name || ""} required autoFocus /></label>
              <label className="field"><span>负责人</span><select name="owner_id" defaultValue={item.owner_id || ""}><option value="">待确认</option>{data.people.map((person) => <option key={person.id} value={person.id}>{person.name}</option>)}</select></label>
              <label className="field"><span>健康状态</span><select name="health" defaultValue={item.health || "待确认"}>{["正常", "风险", "卡住", "完成", "推进中", "初始化中", "待确认"].map((value) => <option key={value}>{value}</option>)}</select></label>
              <label className="field wide"><span>说明与目标</span><textarea name="description" defaultValue={item.description || ""} rows="3" /></label>
              <label className="field"><span>月末目标周期</span><input name="month_goal_period" type="month" defaultValue={item.month_goal_period || ""} /></label>
              <label className="field wide"><span>月末目标</span><textarea name="month_goal" defaultValue={item.month_goal || ""} rows="6" /></label>
              <label className="field wide"><span>本周目标</span><textarea name="week_goal" defaultValue={item.week_goal || ""} rows="3" /></label>
              <label className="field wide"><span>当前重点</span><textarea name="current_focus" defaultValue={item.current_focus || ""} rows="3" /></label>
              <label className="field wide"><span>下一里程碑</span><textarea name="next_milestone" defaultValue={item.next_milestone || ""} rows="2" /></label>
              <label className="field"><span>截止时间</span><input name="due_date" type="date" defaultValue={item.due_date || ""} /></label>
              <label className="field"><span>手动进度 %</span><input name="manual_progress" type="number" min="0" max="100" defaultValue={item.manual_progress ?? ""} placeholder="留空使用任务统计" /></label>
              <label className="field wide"><span>卡点</span><textarea name="blockers" defaultValue={item.blockers || ""} rows="2" /></label>
              <label className="field wide"><span>需要李超拍板</span><textarea name="decisions" defaultValue={item.decisions || ""} rows="2" /></label>
              <label className="field wide"><span>验收证据</span><textarea name="evidence" defaultValue={item.evidence || ""} rows="2" /></label>
            </>
          )}
        </div>
        <div className="modal-footer">
          <span>{isTask ? "完成/取消后仍保留历史" : "归档不会删除任务和记录"}</span>
          <div><button type="button" className="secondary-button" onClick={onClose}>取消</button><button className="primary-button" disabled={saving}>{saving ? <RefreshCw className="spin" size={16} /> : <Check size={16} />}{saving ? "保存中" : "保存"}</button></div>
        </div>
      </form>
    </div>
  );
}

function AppShell({
  children,
  view,
  setView,
  globalSearch,
  setGlobalSearch,
  onNewTask,
  onExport,
  onLogout,
  refreshing,
  onRefresh,
  integration,
  auth,
}) {
  const [mobileMenu, setMobileMenu] = useState(false);
  const visibleNav = navItems.filter((item) =>
    item.adminOnly ? auth.user.is_admin : can(auth, item.id)
  );
  return (
    <div className="app-shell">
      <aside className={`sidebar ${mobileMenu ? "mobile-open" : ""}`}>
        <div className="brand">
          <span className="brand-mark">工</span>
          <div><strong>工作台</strong><small>李超 · 执行管理</small></div>
        </div>
        <nav>
          {visibleNav.map(({ id, label, icon: Icon }) => (
            <button key={id} className={view === id ? "active" : ""} onClick={() => { setView(id); setMobileMenu(false); }}>
              <Icon size={18} /><span>{label}</span>
            </button>
          ))}
        </nav>
        <div className="sidebar-foot">
          <div className="sync-status"><span /><div><strong>SQLite 已连接</strong><small>界面 / CLI / API 同一数据源</small></div></div>
          <div className={`sync-status assistant-sync ${integration?.pending_events ? "pending" : ""}`}>
            <span />
            <div>
              <strong>私人助理已联动</strong>
              <small>{integration?.pending_events ? `${integration.pending_events} 条变更待同步` : "程序与对话双向同步"}</small>
            </div>
          </div>
          {can(auth, "overview", "operate") && (
            <button onClick={onExport}><Download size={16} />导出 Obsidian 摘要</button>
          )}
          <button onClick={onLogout}><LogOut size={16} />退出飞书登录</button>
        </div>
      </aside>
      <div className="main-shell">
        <header className="topbar">
          <button className="mobile-menu-button" onClick={() => setMobileMenu(!mobileMenu)}><Menu size={20} /></button>
          {can(auth, "tasks") ? (
            <label className="global-search">
              <Search size={17} />
              <input value={globalSearch} onChange={(event) => setGlobalSearch(event.target.value)} onKeyDown={(event) => { if (event.key === "Enter") setView("tasks"); }} placeholder="搜索任务、工作块、人员…" />
              <kbd>↵</kbd>
            </label>
          ) : (
            <div className="global-search search-disabled">
              <ShieldCheck size={17} /> 当前按授权范围浏览
            </div>
          )}
          <div className="top-actions">
            <button className="icon-button" title="刷新" onClick={onRefresh}><RefreshCw className={refreshing ? "spin" : ""} size={18} /></button>
            {can(auth, "tasks", "create") && (
              <button className="primary-button quick-add" onClick={onNewTask}><Plus size={16} /><span>新任务</span></button>
            )}
            <Avatar name={auth.user.name} />
          </div>
        </header>
        <main>{children}</main>
      </div>
    </div>
  );
}

export default function App() {
  const initialNavigation = useMemo(
    () => readNavigationState(window.location),
    [],
  );
  const [view, setView] = useState(initialNavigation.view);
  const [auth, setAuth] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [authError, setAuthError] = useState(
    new URLSearchParams(window.location.search).get("auth_error") || "",
  );
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");
  const [toast, setToast] = useState("");
  const [drawer, setDrawer] = useState(initialNavigation.drawer);
  const [modal, setModal] = useState(null);
  const [globalSearch, setGlobalSearch] = useState("");

  const loadAuth = useCallback(async () => {
    setAuthLoading(true);
    try {
      const result = await api("/api/auth/status", {
        headers: { "idempotency-key": "" },
      });
      setAuth(result.data);
      if (window.location.search.includes("auth_error=")) {
        window.history.replaceState(
          window.history.state,
          "",
          removeSearchParameter(window.location, "auth_error"),
        );
      }
    } catch (loadError) {
      setAuthError(loadError.message);
    } finally {
      setAuthLoading(false);
    }
  }, []);

  const load = useCallback(async (quiet = false) => {
    if (!quiet) setLoading(true);
    setRefreshing(true);
    try {
      const result = await api("/api/state", { headers: { "idempotency-key": "" } });
      setData(result.data);
      setError("");
    } catch (loadError) {
      setError(loadError.message);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { loadAuth(); }, [loadAuth]);
  useEffect(() => {
    const syncFromUrl = () => {
      const navigation = readNavigationState(window.location);
      setView(navigation.view);
      setDrawer(navigation.drawer);
    };
    window.addEventListener("popstate", syncFromUrl);
    return () => window.removeEventListener("popstate", syncFromUrl);
  }, []);
  useEffect(() => {
    const nextUrl = navigationUrl(window.location, { view, drawer });
    const currentUrl = `${window.location.pathname}${window.location.search}${window.location.hash}`;
    if (nextUrl !== currentUrl) {
      window.history.replaceState(window.history.state, "", nextUrl);
    }
  }, [view, drawer]);
  useEffect(() => {
    if (!auth?.authenticated) return;
    const allowedViews = navItems
      .filter((item) => item.adminOnly ? auth.user.is_admin : can(auth, item.id))
      .map((item) => item.id);
    if (!allowedViews.length) {
      setLoading(false);
      return;
    }
    if (!allowedViews.includes(view)) setView(allowedViews[0]);
    load();
  }, [auth, load, view]);
  useEffect(() => {
    if (!data || !drawer) return;
    const permitted =
      (drawer.type === "work-block" && can(auth, "work-blocks")) ||
      (drawer.type === "person" && can(auth, "people")) ||
      (drawer.type === "task" &&
        (can(auth, "tasks") || can(auth, "overview") || can(auth, "today")));
    const items = drawer.type === "work-block"
      ? data.work_blocks
      : drawer.type === "person"
        ? data.people
        : data.tasks;
    if (!permitted || !items.some((item) => item.id === drawer.id)) {
      setDrawer(null);
    }
  }, [auth, data, drawer]);
  useEffect(() => {
    if (!toast) return undefined;
    const timer = setTimeout(() => setToast(""), 2600);
    return () => clearTimeout(timer);
  }, [toast]);

  async function saved(message) {
    await load(true);
    setToast(message);
  }

  async function logout() {
    try {
      await api("/api/auth/logout", { method: "POST", body: "{}" });
    } finally {
      setAuth((current) => ({
        ...(current || {}),
        authenticated: false,
        user: null,
        permissions: {},
      }));
      setData(null);
      setDrawer(null);
      setModal(null);
    }
  }

  async function action(task, name) {
    try {
      let payload = {};
      if (name === "block") {
        const blocker = window.prompt("请写明卡点（可稍后在详情中补充）：", task.blocker || "");
        if (blocker === null) return;
        payload = { blocker };
      }
      if (name === "transfer") {
        const assignee = window.prompt("转交给谁？请输入姓名：", "");
        if (!assignee) return;
        payload = { assignee_name: assignee };
      }
      if (name === "postpone") {
        const dueDate = window.prompt("新的截止日期（YYYY-MM-DD）：", task.due_date || "");
        if (!dueDate) return;
        payload = { due_date: dueDate };
      }
      if (name === "cancel" && !window.confirm("确认取消这条任务？历史记录会保留。")) return;
      await api(`/api/tasks/${task.id}/actions/${name}`, { method: "POST", body: JSON.stringify(payload) });
      await saved(name === "complete" ? "任务已完成" : name === "reopen" ? "任务已重新打开" : "任务已更新");
    } catch (actionError) {
      setToast(actionError.message);
    }
  }

  async function archive(block) {
    if (!block.archived && !window.confirm(`确认归档“${block.name}”？任务与历史会保留。`)) return;
    try {
      await api(`/api/work-blocks/${block.id}`, { method: "PATCH", body: JSON.stringify({ archived: !block.archived }) });
      await saved(block.archived ? "工作块已恢复" : "工作块已归档");
    } catch (archiveError) {
      setToast(archiveError.message);
    }
  }

  function open(type, id) {
    const permitted =
      (type === "work-block" && can(auth, "work-blocks")) ||
      (type === "person" && can(auth, "people")) ||
      (type === "task" &&
        (can(auth, "tasks") || can(auth, "overview") || can(auth, "today")));
    if (permitted) setDrawer({ type, id });
  }
  function editTask(task) {
    if (!can(auth, "tasks", "edit")) return;
    setDrawer(null);
    setModal({ type: "task", item: task });
  }
  function editBlock(block) {
    if (!can(auth, "work-blocks", "edit")) return;
    setDrawer(null);
    setModal({ type: "work-block", item: block });
  }

  if (authLoading) {
    return (
      <div className="loading-screen">
        <span className="brand-mark large">工</span>
        <div><strong>正在确认飞书登录状态</strong><p>安全读取用户身份与访问权限…</p></div>
        <span className="loading-line" />
      </div>
    );
  }

  if (!auth?.authenticated) {
    return <LoginScreen configured={auth?.configured} error={authError} />;
  }

  const hasPageAccess =
    auth.user.is_admin ||
    Object.values(auth.permissions || {}).some((permission) => permission.view);
  if (auth.user.status !== "active" || !hasPageAccess) {
    return <PendingAccess user={auth.user} onLogout={logout} />;
  }

  if (loading) {
    return (
      <div className="loading-screen">
        <span className="brand-mark large">工</span>
        <div><strong>正在整理工作状态</strong><p>读取工作块、人员、任务与更新记录…</p></div>
        <span className="loading-line" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="error-screen">
        <AlertCircle size={28} />
        <h1>暂时无法打开工作台</h1>
        <p>{error || "数据加载失败"}</p>
        <button className="primary-button" onClick={() => load()}><RefreshCw size={16} />重新加载</button>
      </div>
    );
  }

  return (
    <AppShell
      view={view}
      setView={setView}
      globalSearch={globalSearch}
      setGlobalSearch={setGlobalSearch}
      onNewTask={() => setModal({ type: "task", item: null })}
      onExport={() => window.open("/api/export", "_blank")}
      onLogout={logout}
      refreshing={refreshing}
      onRefresh={() => load(true)}
      integration={data.integrations?.private_assistant}
      auth={auth}
    >
      {view === "overview" && <Overview data={data} onOpen={open} onEditTask={editTask} onAction={action} onNewTask={() => setModal({ type: "task", item: null })} auth={auth} />}
      {view === "today" && <TodayView data={data} onOpen={open} onAction={action} onSaved={saved} onToast={setToast} auth={auth} />}
      {view === "work-blocks" && <WorkBlocksView data={data} onOpen={open} onEdit={editBlock} onNew={() => setModal({ type: "work-block", item: null })} onArchive={archive} auth={auth} />}
      {view === "people" && <PeopleView data={data} onOpen={open} />}
      {view === "tasks" && <TasksView data={data} globalSearch={globalSearch} onOpen={open} onEdit={editTask} onAction={action} onNew={() => setModal({ type: "task", item: null })} auth={auth} />}
      {view === "events" && <EventsView data={data} />}
      {view === "admin" && <AdminView auth={auth} onToast={setToast} />}
      <DetailDrawer drawer={drawer} data={data} onClose={() => setDrawer(null)} onEditTask={editTask} onEditBlock={editBlock} onAction={action} auth={auth} />
      <FormModal modal={modal} data={data} onClose={() => setModal(null)} onSaved={saved} />
      {toast && <div className="toast"><CheckCircle2 size={17} />{toast}</div>}
    </AppShell>
  );
}
